import { Box, HStack, Th } from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import type { SortDir } from "../lib/tableSort";
import ResizeHandle from "./ResizeHandle";

type SortHeaderProps = {
  label: string;
  active: boolean;
  dir: SortDir;
  onToggle: () => void;
  isNumeric?: boolean;
  // Column resize is opt-in: pass a width + onResize to make the header
  // resizable. Lists without resizable columns (e.g. email templates, origin
  // events) simply omit these and render a plain sortable header.
  width?: number;
  onResize?: (w: number) => void;
  onResetWidth?: () => void;
};

// Shared sortable table header: renders the label, an asc/desc indicator, and
// an optional drag-to-resize handle. Previously each list page defined its own
// copy inside render (which the react-hooks rule flags as a component created
// during render); this lifts the single implementation to module scope.
export default function SortHeader({
  label,
  active,
  dir,
  onToggle,
  isNumeric,
  width,
  onResize,
  onResetWidth,
}: SortHeaderProps) {
  const resizable = onResize !== undefined && width !== undefined;

  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      position={resizable ? "relative" : undefined}
      width={width !== undefined ? `${width}px` : undefined}
      onClick={onToggle}
      _hover={{ bg: "gray.50" }}
    >
      <HStack spacing={1} display="inline-flex" justify={isNumeric ? "flex-end" : "flex-start"}>
        <Box>{label}</Box>
        <Box color={active ? "gray.700" : "gray.300"}>
          {active && dir === "desc" ? (
            <TriangleDownIcon boxSize={2.5} />
          ) : (
            <TriangleUpIcon boxSize={2.5} />
          )}
        </Box>
      </HStack>
      {resizable && (
        <ResizeHandle width={width} onResize={onResize} onResetWidth={onResetWidth ?? (() => {})} />
      )}
    </Th>
  );
}
